import { BROWSER } from '@/constants';
import { ManualUrl, ServiceMode } from '@/constants/plan';
import { browserAxios } from '@/lib/axios';
import { convertObjectToSneakJson } from '@/utils';

import { API_AUTH_RESULT_CODE } from './apiAuth';

/**
 * リクエストパラメータ
 */
export type PlanInfoApiRequest = {
  // 共有画面のURLキー(ホストの場合は未指定、ゲストの場合は設定)
  urlKey?: string;
};

/**
 * 結果コード定数
 */
export const PLAN_INFO_API_RESULT_CODE = {
  // 成功
  OK: 'OK',
  // 入力パラメータエラー
  WARN_INPUT_PARAM: 'WARN_INPUT_PARAM',
  // データ取得で不正なエラー
  WARN_INVALID_DATA: 'WARN_INVALID_DATA',
  // 外部API呼び出し失敗
  WARN_FAILED_CALL: 'WARN_FAILED_CALL',
  // 不明エラー
  ERR_UNKNOWN: 'ERR_UNKNOWN',
  ...API_AUTH_RESULT_CODE,
} as const;
export type PlanInfoResultCode =
  (typeof PLAN_INFO_API_RESULT_CODE)[keyof typeof PLAN_INFO_API_RESULT_CODE];

/**
 * 結果コードのTypeGuard
 *
 * @param resultCode
 * @returns
 */
export const isPlanInfoApiResultCode = (
  resultCode: string,
): resultCode is PlanInfoResultCode => {
  if (resultCode in PLAN_INFO_API_RESULT_CODE) {
    return true;
  }

  return false;
};

/**
 * レスポンス
 */
export type PlanInfoApiResponse = {
  // 結果コード
  resultCode: PlanInfoResultCode;
  // プラン情報
  planInfo: PlanInfo;
};

/**
 * プラン情報のデータ型
 */
export type PlanInfo = {
  // 利用サービス
  service: ServiceMode;
  // お試しプランユーザ
  isTrial: boolean;
  // マニュアル遷移先の設定
  manualUrls: ManualUrl;
  // STTに関する設定
  stt: Stt;
};

/**
 * STTに関する設定のデータ型
 */
export type Stt = {
  // 時間制限
  availableMin?: number;
  // STT連続使用時間
  continueMin?: number;
  // CSVダウンロード
  csvDownload?: boolean;
  // フッターを固定表示
  footerLock?: boolean;
  // STT無音タイムアウト時間
  silenceTimeoutMin?: number;
  // 共有画面に関する設定
  view: View;
};

/**
 * 共有画面に関する設定のデータ型
 */
export type View = {
  // 共有機能利用可能
  enabled: boolean;
  // 一時停止機能利用可能
  pausable?: boolean;
  // ゲスト画面へのSentio案内を表示
  promotion?: boolean;
  // 共有機能利用可能時間
  availableMin?: number;
  // 共有画面が同期されるか
  globalSync?: boolean;
};

/**
 * API 呼び出し
 *
 * @returns
 */
const callApi = (
  request: PlanInfoApiRequest,
  isPrivate = false,
): Promise<PlanInfoApiResponse> => {
  let apiUrl: string = BROWSER.API_URL.PLAN_INFO;
  let params = {};
  if (isPrivate) {
    apiUrl = BROWSER.API_URL.PLAN_INFO_PRIVATE;
  } else {
    // パラメータをスネークケースのJSONに変換
    params = convertObjectToSneakJson(request);
  }

  // リクエスト送信(POST)
  const response = browserAxios
    .post<PlanInfoApiResponse>(apiUrl, params, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((apiResponse) => apiResponse.data);

  return response;
};

/**
 * プラン情報取得API(同通サーバ) リクエスト
 *
 * @returns
 */
export const planInfoApi = (
  request: PlanInfoApiRequest,
  isPrivate?: boolean,
): Promise<PlanInfoApiResponse> => callApi(request, isPrivate ?? false);
